import { listen } from "@tauri-apps/api/event";
import { useEffect } from "react";
import {
  COMPACT_RESOURCE_BROWSER_EVENT,
  COMPACT_RESOURCE_LIFECYCLE_EVENT,
  COMPACT_RESOURCE_THUMBNAIL_EVENT,
  type CompactResourceLifecycle,
  type CompactResourceThumbnail,
} from "./compact-resource-events";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import { isTauri } from "@/lib/is-tauri";
import {
  applyComputerUseLifecycle,
  applyComputerUseThumbnail,
} from "@/lib/computer-use-toast-feed";

/**
 * Feeds computer-use events into work toasts. Mounted once by ToastStack.
 */
export function useComputerUseToastFeed(): void {
  useEffect(() => {
    if (!isTauri()) {
      const handleLifecycle = (event: Event) => {
        applyComputerUseLifecycle(
          (event as CustomEvent<CompactResourceLifecycle>).detail,
          Date.now(),
        );
      };
      const handleThumbnail = (event: Event) => {
        applyComputerUseThumbnail(
          (event as CustomEvent<CompactResourceThumbnail>).detail,
        );
      };
      window.addEventListener(COMPACT_RESOURCE_BROWSER_EVENT, handleLifecycle);
      window.addEventListener(COMPACT_RESOURCE_THUMBNAIL_EVENT, handleThumbnail);
      return () => {
        window.removeEventListener(COMPACT_RESOURCE_BROWSER_EVENT, handleLifecycle);
        window.removeEventListener(COMPACT_RESOURCE_THUMBNAIL_EVENT, handleThumbnail);
      };
    }

    let cancelled = false;
    const unlistens: Array<() => void> = [];
    const addUnlisten = (unlisten: () => void) => {
      if (cancelled) safeTauriUnlisten(unlisten);
      else unlistens.push(unlisten);
    };
    void listen<CompactResourceLifecycle>(
      COMPACT_RESOURCE_LIFECYCLE_EVENT,
      (event) => applyComputerUseLifecycle(event.payload, Date.now()),
    ).then(addUnlisten);
    void listen<CompactResourceThumbnail>(
      COMPACT_RESOURCE_THUMBNAIL_EVENT,
      (event) => applyComputerUseThumbnail(event.payload),
    ).then(addUnlisten);
    return () => {
      cancelled = true;
      unlistens.forEach(safeTauriUnlisten);
    };
  }, []);
}
